import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Paper,
  Grid,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { UpdateCertificateAction } from "../../Redux/Actions/RewardAction";
import { useS3Upload } from "../../hooks/useS3Upload";
import { ApiConfig } from "../../Config/CommonConfig";

interface ConfigField {
  key: string;
  x: number;
  y: number;
  width: number;
  height: number;
  font_family: string;
  font_size: number;
  color: string;
  align: "left" | "center" | "right";
  valign: "top" | "middle" | "bottom";
  placeholder: string;
}

interface EditCertificateModalProps {
  open: boolean;
  onClose: () => void;
  certificate: any;
  onSuccess?: () => void;
}

const DEFAULT_FIELD: ConfigField = {
  key: "",
  x: 0,
  y: 0,
  width: 300,
  height: 50,
  font_family: "Montserrat-Medium.ttf",
  font_size: 31,
  color: "#000000",
  align: "left",
  valign: "top",
  placeholder: "",
};

const EditCertificateModal: React.FC<EditCertificateModalProps> = ({
  open,
  onClose,
  certificate,
  onSuccess,
}) => {
  const dispatch = useDispatch<any>();
  const {
    uploadFile,
    isLoading: s3Loading,
    s3Path,
    success: uploadSuccess,
    error: uploadError,
    resetState: resetS3State,
  } = useS3Upload();

  const [loading, setLoading] = useState(false);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [configFields, setConfigFields] = useState<ConfigField[]>([
    { ...DEFAULT_FIELD },
  ]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    earning_criteria: "",
    organization: "",
    summary: "",
    is_active: true,
    is_expire: false,
    auto_renewal: false,
    allow_revocation: true,
    expiry_duration: "",
  });

  useEffect(() => {
    if (certificate) {
      setFormData({
        name: certificate?.name || "",
        description: certificate?.description || "",
        earning_criteria: certificate?.earning_criteria || "",
        organization: certificate?.organization || "",
        summary: certificate?.summary || certificate?.issuer?.summary || "",
        is_active: certificate?.is_active ?? true,
        is_expire: certificate?.is_expire ?? false,
        auto_renewal: certificate?.auto_renewal ?? false,
        allow_revocation: certificate?.allow_revocation ?? true,
        expiry_duration: certificate?.expiry_duration?.toString() || "",
      });
      if (
        certificate?.config?.fields &&
        Array.isArray(certificate?.config?.fields)
      ) {
        setConfigFields(certificate?.config?.fields);
      }
      if (certificate?.background_url) {
        setImagePreview(certificate?.background_url);
      }
    }
  }, [certificate]);

  useEffect(() => {
    if (!open) {
      resetS3State();
      setTemplateFile(null);
      setImagePreview(certificate?.background_url || "");
    }
  }, [open, certificate, resetS3State]);

  useEffect(() => {
    if (uploadSuccess && s3Path) {
      updateCertificate(s3Path);
    }
  }, [uploadSuccess, s3Path]);

  useEffect(() => {
    if (uploadError && templateFile) {
      setLoading(false);
    }
  }, [uploadError, templateFile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setTemplateFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleAddField = () => {
    setConfigFields([...configFields, { ...DEFAULT_FIELD }]);
  };

  const handleRemoveField = (index: number) => {
    setConfigFields(configFields.filter((_, i) => i !== index));
  };

  const handleFieldChange = (
    index: number,
    field: keyof ConfigField,
    value: any
  ) => {
    const updatedFields = [...configFields];
    updatedFields[index] = {
      ...updatedFields[index],
      [field]: value,
    };
    setConfigFields(updatedFields);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (templateFile) {
        uploadFile(
          ApiConfig?.BadgeUploadPresigned,
          templateFile,
          templateFile?.type
        );
      } else {
        updateCertificate(certificate.background_url);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const updateCertificate = async (backgroundUrl: string) => {
    try {
      const updateData: any = {};

      if (formData?.name !== certificate?.name) {
        updateData.name = formData?.name;
      }
      if (formData?.description !== certificate?.description) {
        updateData.description = formData?.description;
      }
      if (formData?.earning_criteria !== certificate?.earning_criteria) {
        updateData.earning_criteria = formData?.earning_criteria;
      }
      if (formData?.organization !== certificate?.organization) {
        updateData.organization = formData?.organization;
      }
      if (
        formData?.summary !==
        (certificate?.summary || certificate?.issuer?.summary)
      ) {
        updateData.summary = formData?.summary;
      }
      if (backgroundUrl !== certificate?.background_url) {
        updateData.background_url = backgroundUrl;
      }
      if (formData?.is_active !== certificate?.is_active) {
        updateData.is_active = formData?.is_active;
      }
      if (formData?.is_expire !== certificate?.is_expire) {
        updateData.is_expire = formData?.is_expire;
      }
      if (formData?.auto_renewal !== certificate?.auto_renewal) {
        updateData.auto_renewal = formData?.auto_renewal;
      }
      if (formData?.allow_revocation !== certificate?.allow_revocation) {
        updateData.allow_revocation = formData?.allow_revocation;
      }
      if (
        JSON.stringify(configFields) !==
        JSON.stringify(certificate.config?.fields)
      ) {
        updateData.config = {
          fields: configFields,
        };
      }

      if (formData?.is_expire && formData?.expiry_duration) {
        if (
          formData?.expiry_duration !== certificate?.expiry_duration?.toString()
        ) {
          updateData.expiry_duration = Number(formData?.expiry_duration);
        }
      }

      const result = await dispatch(
        UpdateCertificateAction(certificate?.id, updateData)
      );
      if (result && result.status === true) {
        resetForm();
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTemplateFile(null);
    setImagePreview(certificate?.background_url || "");
    setLoading(false);
    resetS3State();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getImageFilename = (url: string) => {
    if (!url) return "";
    const urlWithoutParams = url?.split("?")[0];
    const parts = urlWithoutParams?.split("/");
    const filename = parts[parts?.length - 1];
    return decodeURIComponent(filename);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Edit Certificate: {certificate?.code}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: "#0555c8" }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Certificate Name"
                  value={formData?.name}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  value={formData?.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="earning_criteria"
                  label="Earning Criteria"
                  value={formData?.earning_criteria}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="organization"
                  label="Organization"
                  value={formData?.organization}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="summary"
                  label="Summary"
                  value={formData?.summary}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: "#0555c8" }}>
              Certificate Template
            </Typography>
            {imagePreview && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                  <img
                    src={imagePreview}
                    alt="Template preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 300,
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Current: {getImageFilename(imagePreview)}
                </Typography>
              </Box>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ width: "100%" }}
            />
            {templateFile && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", color: "#0555c8" }}
              >
                New file selected: {templateFile?.name}
              </Typography>
            )}
            {uploadError && templateFile && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", color: "error.main" }}
              >
                Upload Error: {uploadError}
              </Typography>
            )}
          </Box>

          <Divider />

          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: "#0555c8" }}>
                Certificate Fields Configuration
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddField}
                variant="outlined"
                size="small"
              >
                Add Field
              </Button>
            </Box>

            {configFields?.map((field, index) => (
              <Paper
                key={index}
                sx={{ p: 2, mb: 2, backgroundColor: "#f9f9f9" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Field {index + 1}
                  </Typography>
                  <IconButton
                    onClick={() => handleRemoveField(index)}
                    color="error"
                    size="small"
                    disabled={configFields?.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Field Key"
                      value={field?.key}
                      onChange={(e) =>
                        handleFieldChange(index, "key", e.target.value)
                      }
                      fullWidth
                      size="small"
                      placeholder="e.g., issued_date, name"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Placeholder"
                      value={field?.placeholder}
                      onChange={(e) =>
                        handleFieldChange(index, "placeholder", e.target.value)
                      }
                      fullWidth
                      size="small"
                      placeholder="e.g., Nov 14, 2024"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="X Position"
                      type="number"
                      value={field?.x}
                      onChange={(e) =>
                        handleFieldChange(index, "x", Number(e.target.value))
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="Y Position"
                      type="number"
                      value={field?.y}
                      onChange={(e) =>
                        handleFieldChange(index, "y", Number(e.target.value))
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="Width"
                      type="number"
                      value={field?.width}
                      onChange={(e) =>
                        handleFieldChange(
                          index,
                          "width",
                          Number(e.target.value)
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="Height"
                      type="number"
                      value={field?.height}
                      onChange={(e) =>
                        handleFieldChange(
                          index,
                          "height",
                          Number(e.target.value)
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="Font Size"
                      type="number"
                      value={field?.font_size}
                      onChange={(e) =>
                        handleFieldChange(
                          index,
                          "font_size",
                          Number(e.target.value)
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      label="Color"
                      type="color"
                      value={field?.color}
                      onChange={(e) =>
                        handleFieldChange(index, "color", e.target.value)
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Horizontal Align</InputLabel>
                      <Select
                        value={field?.align}
                        onChange={(e) =>
                          handleFieldChange(index, "align", e.target.value)
                        }
                        label="Horizontal Align"
                      >
                        <MenuItem value="left">Left</MenuItem>
                        <MenuItem value="center">Center</MenuItem>
                        <MenuItem value="right">Right</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Vertical Align</InputLabel>
                      <Select
                        value={field?.valign}
                        onChange={(e) =>
                          handleFieldChange(index, "valign", e.target.value)
                        }
                        label="Vertical Align"
                      >
                        <MenuItem value="top">Top</MenuItem>
                        <MenuItem value="middle">Middle</MenuItem>
                        <MenuItem value="bottom">Bottom</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Font Family"
                      value={field?.font_family}
                      onChange={(e) =>
                        handleFieldChange(index, "font_family", e.target.value)
                      }
                      fullWidth
                      size="small"
                      placeholder="e.g., Montserrat-Medium.ttf"
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: "#0555c8" }}>
              Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_active"
                      checked={formData?.is_active}
                      onChange={handleChange}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_expire"
                      checked={formData?.is_expire}
                      onChange={handleChange}
                    />
                  }
                  label="Has Expiry"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      name="auto_renewal"
                      checked={formData?.auto_renewal}
                      onChange={handleChange}
                    />
                  }
                  label="Auto Renewal"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      name="allow_revocation"
                      checked={formData?.allow_revocation}
                      onChange={handleChange}
                    />
                  }
                  label="Allow Revocation"
                />
              </Grid>
              {formData?.is_expire && (
                <Grid item xs={12} md={6}>
                  <TextField
                    name="expiry_duration"
                    label="Expiry Duration (days)"
                    type="number"
                    value={formData?.expiry_duration}
                    onChange={handleChange}
                    fullWidth
                    helperText="Number of days until certificate expires"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || s3Loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || s3Loading || !formData?.name}
          sx={{
            backgroundColor: "#0555c8",
            "&:hover": {
              backgroundColor: "#043c99",
            },
          }}
        >
          {loading || s3Loading ? <CircularProgress size={24} /> : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCertificateModal;
